import { RefObject, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router-dom';
import axios from '@/_utils/axios';
import { buildV2Order, cleanDates, getCleanedVersionKeys, validateV2Jobs } from '@/_utils/orderUtils';
import { selectBulkAutoSubmit, selectBulkTitleErrors, selectBulkUploadedFile } from '@/redux/selectors/bulkSearchSelectors';
import {
  selectInventory,
  selectInventorySelections,
  selectOutputVersions,
  selectStep,
  selectTitleInput,
  selectVersions,
  selectDraftState,
  selectSchemaFormRefs
} from '@/redux/selectors/orderPlacementSelectors';
import { clearBulkErrors, resetBulkSearch } from '@/redux/slices/bulkSearchSlice';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { selectActiveTitleId, selectTitle, selectTitleDataById } from '@/redux/selectors/titleSelectors';
import {
  clearValidationErrors,
  resetInventorySelection,
  setDraftState,
  setNewOrderId,
  setStep,
  clearOrderPlacement,
  toggleCancelationDialog,
  toggleFormIsDraft,
  toggleFormIsIncomplete,
  setValidationErrors,
  setToggledTitleSearchMode
} from '@/redux/slices/orderPlacementSlice';
import { FormValues, JobDrafts } from '@/redux/slices/slice_types';
import { clearTitle } from '@/redux/slices/titleSlice';
import PolarisIcon from '../_common/PolarisIcon';
import Dialog from '../_common/Dialog';
import Spinner from '../_common/Spinner';
import { useToast } from '../_common/Toast';
import { useConfigValue } from '../_hooks/useConfigValue';
import useProfile from '../_hooks/useProfile';
import { useFile } from '@/contextAPI/fileContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useGoToCreatedOrder } from '@/components/OrderPlacement/hooks/useGoToCreatedOrder.hook';
import StepperButton from '../_common/StepperButton';
import { OrderPlacementConfig, TitleSearchMode } from '@/__configs/config_types';
import { isVersion, getLineageMetaTitle as getLineageMetaTitleDefault } from '@/_utils/utils';

type OPOrderStepperButtonsProps = {
  jobs?: JobDrafts;
  haveJobsBeenEdited?: boolean;
  schemaRefs?: RefObject<any>;
  onClearFilters?: () => void;
  onPerformValidations?: () => void;
};

const OPOrderStepperButtons = ({ jobs, haveJobsBeenEdited, onClearFilters, schemaRefs, onPerformValidations }: OPOrderStepperButtonsProps) => {
  const [showEmbargoWarning, setShowEmbargoWarning] = useState(false);
  const [embargoedTitles, setEmbargoedTitles] = useState<{ label: string; ids: string }[]>([]);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const orderPlacementVersion = useConfigValue(['orderPlacementConfig', 'version']) || 'v2';
  const { values, setValues, validateForm, resetForm } = useFormikContext<FormValues>();
  const [continueAnyway, setContinueAnyway] = useState(false);
  const [continueAnywayProfile, setContinueAnywayProfile] = useState(false);
  const [openSaveAsDraftDialog, setOpenSaveAsDraftDialog] = useState(false);
  const [backToStepTwo, setBackToStepTwo] = useState(false);
  const [continueWithBulkErrors, setContinueWithBulkErrors] = useState(false);
  const versionSelections = useAppSelector(selectVersions); // if this is version flow, these are the step 2 selections
  const outputVersions = useAppSelector(selectOutputVersions); // if this is componentized flow, these are the step 2 selections
  const inventory = useAppSelector(selectInventory);
  const titleInput = useAppSelector(selectTitleInput);
  const activeTitle = useAppSelector(selectTitle);
  const titleDataById = useAppSelector(selectTitleDataById);
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const inventorySelections = useAppSelector(selectInventorySelections);
  const step = useAppSelector(selectStep);
  const prevDraftState = useAppSelector(selectDraftState);
  const { error } = useToast();
  const [loadingMessage, setLoadingMessage] = useState<undefined | string>();
  const checkEmbargoedVersions = useConfigValue<OrderPlacementConfig['checkEmbargoedVersions']>(['orderPlacementConfig', 'checkEmbargoedVersions']);
  const disableDueDate = useConfigValue<OrderPlacementConfig['disableDueDate']>(['orderPlacementConfig', 'disableDueDate']);
  const disableEmbargoDate = useConfigValue<OrderPlacementConfig['disableEmbargoDate']>(['orderPlacementConfig', 'disableEmbargoDate']);
  const draftState = useAppSelector(selectDraftState);
  const bulkTitleSearchOptions = useConfigValue<OrderPlacementConfig['bulkTitleSearchOptions']>(['orderPlacementConfig', 'bulkTitleSearchOptions']);
  const addlFields = useConfigValue<OrderPlacementConfig['addlFields']>(['orderPlacementConfig', 'addlFields']);
  const { file, setFile } = useFile();
  const formRefs = useAppSelector(selectSchemaFormRefs);
  const getLineageMetaTitle = useConfigValue<OrderPlacementConfig['getLineageMetaTitle']>(['orderPlacementConfig', 'getLineageMetaTitle']);

  // bulk order
  const { isBulkBased } = useProfile(values);
  const bulkUploadedFile = useAppSelector(selectBulkUploadedFile);
  const bulkAutoSubmit = useAppSelector(selectBulkAutoSubmit);
  const bulkSearchErrors = useAppSelector(selectBulkTitleErrors);
  const goToCreatedOrder = useGoToCreatedOrder();

  const getVersionTitle = (ver: any) => {
    const titleName = getLineageMetaTitle ? getLineageMetaTitle(ver) : getLineageMetaTitleDefault(isVersion(ver) ? ver : undefined);

    return titleName;
  };

  const buildEmbargoedTitle = (titleStr: string, ver: any) => {
    const ids = [ver?.external_id, ver?.rightsline_id, ver?.phoenix7_id].filter(Boolean).join(' | ');
    return { label: titleStr, ids };
  };

  // if the user has added versions that have `is_embarogoed` flag, we want to show a warning before they submit the order
  const findEmbargoedTitles = () => {
    let embargoed: { label: string; ids: string }[] = [];
    if (jobs && Object.keys(jobs).length > 0) {
      embargoed = Object.values(jobs)
        .filter((job: any) => job.version && (job.version.is_embargoed || job.version.IsEmbargo || job.version.isEmbargoed))
        .map((job: any) => {
          const titleStr = `${getVersionTitle(job.version)} ${job.version?.name || job.version?.external_id || job.version?.id || 'Unknown'}`;
          return buildEmbargoedTitle(titleStr, job.version);
        });
    }
    if (outputVersions && outputVersions.length > 0) {
      embargoed = embargoed.concat(
        outputVersions
          .filter((ver: any) => ver.is_embargoed || ver.IsEmbargo || ver.isEmbargoed)
          .map((ver: any) => {
            const titleStr = `${getVersionTitle(ver)} ${ver.name || ver.external_id || ver.id || 'Unknown'}`;
            return buildEmbargoedTitle(titleStr, ver);
          })
      );
    }
    if (versionSelections && versionSelections.length > 0) {
      embargoed = embargoed.concat(
        versionSelections
          .filter((ver: any) => ver.is_embargoed || ver.IsEmbargo || ver.isEmbargoed)
          .map((ver: any) => {
            const titleStr = `${getVersionTitle(ver)} ${ver.name || ver.external_id || ver.id || 'Unknown'}`;
            return buildEmbargoedTitle(titleStr, ver);
          })
      );
    }
    return embargoed.filter((item, i, arr) => arr.findIndex((x) => x.label === item.label) === i);
  };

  const confirmUnsavedProfile = () => {
    setValues({ ...values, jobs: values.jobs?.slice(0, -1) });
    onNext();
    setContinueAnywayProfile(false);
  };

  const confirmContinueAnyway = () => {
    dispatch(resetInventorySelection());
    onNext();
    setContinueAnyway(false);
  };

  const confirmContinueWithBulkErrors = () => {
    dispatch(clearBulkErrors());
    onNext();
    setContinueWithBulkErrors(false);
  };

  const validateOrderDetails = () => {
    if (onPerformValidations && onPerformValidations()) {
      return;
    }

    validateForm().then((errorResult: any) => {
      // we only require 1 job, so if we have more, ignore the rest
      if (errorResult?.jobs?.length > 1) {
        delete errorResult.jobs;
      }
      if (errorResult && Object.keys(errorResult).length) {
        error('Some required fields are missing');
      } else {
        // check if the last profile details has unsaved data
        const lastJobIsEmpty =
          values.jobs.length > 1 && (!values?.jobs[values?.jobs?.length - 1]?.customer || !values?.jobs[values?.jobs?.length - 1]?.profile);
        if (lastJobIsEmpty) {
          setContinueAnywayProfile(true);
        } else {
          onNext();
        }
      }
    });
  };

  const validateInventoryAndContinue = () => {
    if (bulkSearchErrors?.length) {
      setContinueWithBulkErrors(true);
    } else if (inventorySelections.length) {
      setContinueAnyway(true);
    } else {
      onNext();
    }
  };

  const cancel = () => {
    history.push('/orders');
  };

  const backToOrdersAfterDraft = (id: string) => {
    dispatch(setCurrentTable('orders'));
    dispatch(clearFilters());
    dispatch(applyFilter({ type: 'filter_id', selections: [id, 'order_hid_filter'] }));
    dispatch(toggleFormIsDraft(false));
    cleanOrderPlacement();
    dispatch(setStep(0));
    history.push('/orders');
  };

  // even if we are submitting the order, we must first create a draft
  // and then submit the draft immediately
  const submitDraft = (isDraft = false) => {
    setLoadingMessage(isDraft ? 'Saving order draft...' : 'Submitting order...');
    // if we're passing in jobs, we know this is a version/simple order
    // because componentized/advanced orders don't have generated jobs
    const orderFields = buildV2Order(values, jobs, outputVersions, addlFields);
    orderFields.order_requests = cleanDates(orderFields.order_requests, disableDueDate, disableEmbargoDate);

    const order = {
      ...orderFields,
      order_type: 'standard',
      due_date: values?.jobs?.[0]?.due_date,
      draft_state: {
        ...draftState,
        titleSlice: {
          titleDataById,
          activeTitleId,
          activeTitle
        },
        values,
        titleInput,
        inventorySelections,
        inventory,
        outputVersions,
        versionSelections,
        versionKeys: getCleanedVersionKeys(jobs),
        jobs,
        step,
        profileData: {
          ...values.jobs?.[0]?.profile?.data,
          id: values.jobs?.[0]?.profile?.id,
          customer: values.jobs?.[0]?.profile?.customer_name,
          name: values.jobs?.[0]?.profile?.name,
          label: values.jobs?.[0]?.profile?.label
        }
      }
    };
    if (isBulkBased) {
      order.auto_submit_jobs = bulkAutoSubmit;
      order.draft_state.autoSubmit = bulkAutoSubmit;
      order.draft_state.file = bulkUploadedFile;
      order.embargo_date = values?.jobs?.[0]?.embargo_date;
      order.order_type = 'bulk';
    }

    // check if the user is editing a draft or creating a new one
    let url: string;

    const draftId = prevDraftState?.draftId || draftState?.draftId;
    if (draftId) {
      url = '/orders/' + draftId + '/';
    } else {
      url = '/orders/create/';
    }

    return axios({ method: 'post', baseURL: '/api/v2/core/', url, data: order })
      .then((res) => {
        const result = res.data?.data;
        if (!result?.id) {
          throw new Error();
        } else {
          dispatch(toggleFormIsIncomplete(false));
          dispatch(toggleCancelationDialog(false));
          dispatch(setNewOrderId(result?.hid));
          dispatch(setDraftState({ draftId: result?.id }));
          setLoadingMessage(undefined);
          setOpenSaveAsDraftDialog(false);
          if (isDraft) {
            backToOrdersAfterDraft(result?.hid);
          }
          return result;
        }
      })
      .catch(() => {
        error('There was a problem placing your order.');
        setLoadingMessage(undefined);
        setOpenSaveAsDraftDialog(false);
      });
  };

  const validateCustomForms = () => {
    let hasInvalidForm = false;
    if (formRefs && formRefs.size > 0) {
      const refs = formRefs.values();
      refs.forEach((formRef) => {
        const hasErrors = formRef.current && !formRef.current?.validateForm();
        if (hasErrors) {
          hasInvalidForm = true;
        }
      });
    }
    return hasInvalidForm;
  };

  // Intercept submit to check embargoed titles
  const onSubmit = () => {
    const embargoed = findEmbargoedTitles();
    if (embargoed.length > 0 && !showEmbargoWarning) {
      setEmbargoedTitles(embargoed);
      setShowEmbargoWarning(true);
      return;
    }
    // VALIDATION: there are several things happening here to validate jobs
    // 1. validate the formik form with `validateForm()` which at this point is ONLY validating the Order Details
    // 2. validate the common jobs fields with `validateV2Jobs()` which returns an array of field errors (currently only `profile`)
    //    this sets the error styling on the individual job fields
    // 3. validate the instance_inputs by programmatically validating the SchemaForms
    // 4. if any of the 3 above return any errors, we set `hasInvalidForm` to true which will prevent the order from being submitted
    //    as well as set the error styling on the job accordion (red border-left)

    if (validateCustomForms()) {
      return;
    }

    dispatch(clearValidationErrors());

    const jobFieldErrors = isBulkBased ? [] : validateV2Jobs(jobs); // get errors for the common job fields
    validateForm().then((errorResult: any) => {
      // get errors for the Order Details
      let hasInvalidForm = false;

      if (!isBulkBased) {
        delete errorResult.jobs; // we do not validate jobs this way because we have more complex schema validation
        if (jobFieldErrors.length > 0) {
          hasInvalidForm = true;
          jobFieldErrors.forEach((error) => {
            dispatch(setValidationErrors(error));
          });
        }
        if (errorResult && Object.keys(errorResult).length) {
          hasInvalidForm = true;
        }
        if (schemaRefs?.current) {
          Object.keys(schemaRefs.current).forEach((formRef) => {
            const isValid = schemaRefs.current[formRef].current?.validateForm();
            if (!!schemaRefs.current[formRef].current && !isValid) hasInvalidForm = true;
          });
        }
      }

      if (hasInvalidForm) {
        // if any of the above validations return errors, we do not submit the order
        error('Some fields have errors. Please fix them before submitting.');
        if (onClearFilters) {
          onClearFilters();
        }
        return;
      } else {
        submitDraft().then((draft) => {
          if (draft) {
            setLoadingMessage('Submitting order...');
            const order = buildV2Order(values, jobs, outputVersions, addlFields);
            order.order_requests = cleanDates(order.order_requests, disableDueDate, disableEmbargoDate);
            submitOrder(draft, order);
          }
        });
      }
    });
  };

  const submitFile = async (draft: any) => {
    const profileId = (values as { jobs: any[] }).jobs?.[0]?.profile?.id;
    if (!profileId) return;
    const data = new FormData();
    data.append('input_file', file);
    data.append('profile_id', profileId);
    data.append('due_date', values?.jobs?.[0]?.due_date);
    if (values?.jobs?.[0]?.embargo_date) data.append('embargo_date', new Date(values?.jobs?.[0]?.embargo_date).toISOString());

    await axios({
      method: 'post',
      baseURL: '/api/v2/core/',
      url: `/orders/${draft.id}/bulk-file-upload/`,
      data: data
    })
      .then(() => {
        setFile({});
      })
      .catch((error: any) => {
        error('There was a problem uploading the file.');
      });
  };

  const submitOrder = (draft: any, order: any) => {
    axios({
      method: 'post',
      baseURL: `/api/${orderPlacementVersion}/core/`,
      url: `/orders/${draft.id}/submit/`,
      data: {
        ...order,
        due_date: values?.jobs?.[0]?.due_date
      }
    })
      .then((res): Promise<string> => {
        if (isBulkBased) {
          return submitFile(draft).then(() => res.data.data.hid);
        }

        return res.data.data.hid;
      })
      .then((newOrderId: string) => {
        cleanOrderPlacement();
        setLoadingMessage(undefined);

        if (isBulkBased) {
          goToCreatedOrder(newOrderId);
        } else {
          resetForm();
          dispatch(setStep(3));
          dispatch(setNewOrderId(newOrderId));
        }
      })
      .catch(() => {
        error('There was a problem placing your order.');
        setLoadingMessage(undefined);
      });
  };

  const onNext = () => {
    if (bulkTitleSearchOptions?.size && step === 0) {
      if (isBulkBased) {
        dispatch(setToggledTitleSearchMode(TitleSearchMode.BULK_OR_PASTE));
      } else {
        dispatch(setToggledTitleSearchMode(TitleSearchMode.DEFAULT));
      }
    }

    dispatch(setStep(step + 1));
  };

  const onBack = () => {
    if (step === 2 && haveJobsBeenEdited) {
      setBackToStepTwo(true);
    } else {
      dispatch(setStep(step - 1));
    }
  };

  const onSaveDraft = () => {
    dispatch(toggleFormIsDraft(true));
    setOpenSaveAsDraftDialog(true);
  };

  const cleanOrderPlacement = () => {
    dispatch(clearOrderPlacement());
    dispatch(clearTitle());
    dispatch(resetBulkSearch());
  };

  const isNextDisabled = useMemo(() => {
    if (step !== 1) {
      return false;
    }

    return !outputVersions.length && !versionSelections.length;
  }, [step, outputVersions, versionSelections]);

  const isDisabled = isBulkBased ? true : !values.description;

  const showSaveAndContinue = useMemo(() => {
    return step !== (isBulkBased ? 1 : 2);
  }, [step, isBulkBased, bulkTitleSearchOptions]);

  const disableSubmit = useMemo(() => {
    if (isBulkBased) {
      return !bulkUploadedFile?.name;
    }

    return jobs && !outputVersions.length && !Object.keys(jobs)?.length;
  }, [isBulkBased, bulkUploadedFile, jobs, outputVersions]);

  const getOnCompleteAction = () => {
    if (showSaveAndContinue) {
      if (step === 0) {
        return validateOrderDetails;
      }
      if (step === 1) {
        return validateInventoryAndContinue;
      }

      return onNext;
    } else {
      return onSubmit;
    }
  };
  return (
    <>
      <Dialog
        open={!!checkEmbargoedVersions && showEmbargoWarning}
        title="⚠️ This order contains embargoed titles"
        submitText="Acknowledge & Continue"
        onSubmit={() => {
          setShowEmbargoWarning(false);
          setTimeout(() => onSubmit(), 0);
        }}
        cancelText="Cancel"
        onCancel={() => setShowEmbargoWarning(false)}
        maxWidth={480}
      >
        <div>
          One or more versions in this order are flagged as embargoed.
          <br />
          <strong>Embargoed Titles:</strong>
          <ul style={{ marginTop: 8 }}>
            {embargoedTitles.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong>
                {item.ids && <div>{item.ids}</div>}
              </li>
            ))}
          </ul>
          Please confirm you wish to proceed.
        </div>
      </Dialog>
      <StepperButton
        back={{
          'data-testid': 'back-button',
          onClick: step !== 0 ? onBack : undefined,
          label: 'Back'
        }}
        cancel={{
          'data-testid': 'cancel-button',
          label: 'Cancel',
          onClick: cancel
        }}
        complete={{
          'data-testid': 'complete-button',
          endIcon: showSaveAndContinue ? <PolarisIcon icon="right" height={15} /> : undefined,
          disabled: showSaveAndContinue ? isNextDisabled : disableSubmit,
          label: showSaveAndContinue ? 'Save & Continue' : 'Submit Order',
          type: showSaveAndContinue ? 'submit' : undefined,
          onClick: getOnCompleteAction()
        }}
        draft={{
          'data-testid': 'draft-button',
          disabled: isDisabled,
          label: 'Save as Draft',
          tooltip: isDisabled && isBulkBased ? 'Bulk orders cannot be saved as draft' : undefined,
          onClick: onSaveDraft
        }}
      />
      <Dialog
        open={continueAnywayProfile}
        title="Unsaved Profile Details"
        titleIcon={<ProblemIcon icon="error" />}
        submitText="Continue &amp; Discard"
        onSubmit={confirmUnsavedProfile}
        cancelText="Cancel"
        onCancel={() => setContinueAnywayProfile(false)}
      >
        Unsaved profile details will be lost.
      </Dialog>
      <Dialog
        open={continueAnyway}
        title="Incomplete Output Version"
        titleIcon={<ProblemIcon icon="error" />}
        submitText="Continue &amp; Discard"
        onSubmit={confirmContinueAnyway}
        cancelText="Cancel"
        onCancel={() => setContinueAnyway(false)}
      >
        File Selections have not been associated to an output version and/or added to the order.
      </Dialog>
      <Dialog
        open={continueWithBulkErrors}
        title="You have unresolved invalid IDs"
        submitText="Leave Page"
        onSubmit={confirmContinueWithBulkErrors}
        cancelText="Stay on page"
        onCancel={() => setContinueWithBulkErrors(false)}
      >
        Invalid IDs will not be added to your order
      </Dialog>
      <Dialog
        open={openSaveAsDraftDialog}
        title="Save order as draft"
        titleIcon={<DraftIcon icon="draft" />}
        submitText="Save as Draft"
        onSubmit={() => {
          submitDraft(true);
          setOpenSaveAsDraftDialog(false);
        }}
        cancelText="Cancel"
        onCancel={() => {
          setOpenSaveAsDraftDialog(false);
          dispatch(toggleFormIsDraft(false));
        }}
      >
        Confirm you want to save &quot;{values.description}&quot; as a draft?
      </Dialog>
      <Dialog
        open={backToStepTwo}
        title="Are you sure you want to go back?"
        submitText="Yes, discard job edits"
        onSubmit={() => dispatch(setStep(step - 1))}
        cancelText="No, cancel"
        onCancel={() => setBackToStepTwo(false)}
      >
        All job-specific edits will be discarded.
      </Dialog>
      {!!loadingMessage && <Spinner label={loadingMessage} />}
    </>
  );
};

export default OPOrderStepperButtons;

const ProblemIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.warning.main};
  height: 1.5rem;
  width: 1.5rem;
  margin-right: 0.5rem;
`;

const DraftIcon = styled(PolarisIcon)`
  height: 1.5rem;
  width: 1.5rem;
  margin-right: 0.5rem;
`;
